import axios from "axios";

// Define API base URL - using environment variable or fallback to domain
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_DOMAIN || '';

// Simplified API for internal use
let api_token = "default_api_token";

// Initialize API token
export const initializeApiToken = () => {
  const storedApiToken = localStorage.getItem("api_token");
  if (!storedApiToken) {
    // Set default if not found
    localStorage.setItem("api_token", api_token);
  } else {
    api_token = storedApiToken;
  }
};

// Authentication
export const getAccessToken = async () => {
  try {
    return localStorage.getItem('token');
  } catch (error) {
    console.error("Error obtaining access token:", error);
    throw new Error("Failed to obtain access token");
  }
};

// Function to upload file with progress tracking
export const uploadFile = async (file, chatId, onProgressUpdate) => {
  try {
    // Split file into chunks for larger files (1MB chunks)
    const CHUNK_SIZE = 1024 * 1024; // 1MB
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    //console.log(`Initializing upload for file "${file.name}" (${file.size} bytes, ${totalChunks} chunks) to chat ${chatId}`);
    
    // Get access token
    const token = await getAccessToken();
    
    // Log for debugging
    //console.log("Current API base URL:", API_BASE_URL);
    
    // Initialize upload - using the full URL with API prefix
    const initResponse = await fetch(`${API_BASE_URL}/assistant/api/files/initialize_upload/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        chat: chatId,
        filename: file.name,
        total_chunks: totalChunks,
        file_size: file.size
      })
    });
    
    if (!initResponse.ok) {
      throw new Error('Failed to initialize file upload');
    }
    
    const initData = await initResponse.json();
    const fileId = initData.file_id;
    
    // Upload chunks with progress updates
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(file.size, start + CHUNK_SIZE);
      const chunk = file.slice(start, end);
      
      const formData = new FormData();
      formData.append('file_id', fileId);
      formData.append('chunk_index', i);
      formData.append('chunk', chunk);
      
      const chunkResponse = await fetch(`${API_BASE_URL}/assistant/api/files/upload_chunk/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await getAccessToken()}`
        },
        body: formData
      });
      
      if (!chunkResponse.ok) {
        throw new Error(`Failed to upload chunk ${i}`);
      }
      
      // Update progress
      const progress = Math.round(((i + 1) / totalChunks) * 90); // Go up to 90%
      if (onProgressUpdate) {
        onProgressUpdate({
          progress,
          status: 'uploading'
        });
      }
      
      const chunkData = await chunkResponse.json();
      
      // If this was the last chunk and we got a complete status, return the file data
      if (i === totalChunks - 1 && chunkData.status === 'upload_complete') {
        return chunkData.file;
      }
    }
    
    // Final progress update
    if (onProgressUpdate) {
      onProgressUpdate({
        progress: 95,
        status: 'finalizing'
      });
    }
    
    // Check final status
    const statusResponse = await fetch(`${API_BASE_URL}/assistant/api/files/check_upload_status/?file_id=${fileId}`, {
      headers: {
        'Authorization': `Bearer ${await getAccessToken()}`
      }
    });
    
    if (!statusResponse.ok) {
      throw new Error('Failed to check upload status');
    }
    
    const statusData = await statusResponse.json();
    
    if (statusData.status === 'upload_complete' && statusData.file) {
      return statusData.file;
    } else {
      // If we got here, the file should exist in the backend but we didn't get the file data
      // Let's fetch the file data directly
      try {
        const filesResponse = await fetch(`${API_BASE_URL}/assistant/api/files/?chat=${chatId}`, {
          headers: {
            'Authorization': `Bearer ${await getAccessToken()}`
          }
        });
        
        if (filesResponse.ok) {
          const files = await filesResponse.json();
          const uploadedFile = files.find(f => f.file_id === fileId || f.id === fileId);
          
          if (uploadedFile) {
            return uploadedFile;
          }
        }
      } catch (e) {
        console.error("Error fetching uploaded file details:", e);
      }
      
      // If we couldn't get file details, return a minimal object with the ID
      return { 
        id: fileId, 
        file_id: fileId,
        filename: file.name,
        process_status: 'pending'
      };
    }
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

export const listFiles = async (chatId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/assistant/api/files/?chat=${chatId}`, {
      headers: {
        'Authorization': `Bearer ${await getAccessToken()}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch files');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error listing files:', error);
    throw error;
  }
};

export const deleteFile = async (fileId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/assistant/api/files/${fileId}/`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${await getAccessToken()}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete file');
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

// Helper to clean chat IDs - some IDs have an underscore followed by a UUID suffix
// We need to extract just the first part before the underscore for backend compatibility
export const cleanChatId = (chatId) => {
  if (!chatId) return chatId;
  return chatId.includes('_') ? chatId.split('_')[0] : chatId;
};

// Export API_BASE_URL for use elsewhere
export { API_BASE_URL }; 